/**
 * 시작 템플릿 — 빈 화면 대신 "목적"을 고르면 블록이 깔린 상태로 시작.
 * 2026-07-23 activation 1순위(전략문서): Studio가 1등인데 빈 워크스페이스로 시작해 첫 결과까지 오래 걸림.
 *
 * ⭐ 설계: 기본 프로젝트(고양이·배경 자산 그대로) 위에 **블록 JSON만 얹는다**.
 *    → 별도 .sb3 자산 불필요, 용량 0, 자산 깨질 일 없음. defaultProject의 projectJson을 받아 blocks만 채운다.
 *
 * 블록 JSON 스키마(sb3 project.json): {opcode, next, parent, inputs, fields, shadow, topLevel, x, y}
 *  - inputs 숫자값: [1, [4, '10']]  (1=shadow만, 4=math_number)
 *  - inputs 문자값: [1, [10, 'text']] (10=text)
 *  - 블록 붙이기: A.next = B.id, B.parent = A.id
 */

// 고유 id 생성기(프로젝트 내에서만 유일하면 됨)
const mk = prefix => {
    let n = 0;
    return () => `curo-${prefix}-${n++}`;
};

// ⚠️ sb3 파일 형식([1,[4,'10']])은 project.json 파싱 경로에서만 쓰인다.
//    우리는 createBlock 으로 직접 넣으므로 **VM 내부 형식**을 써야 한다:
//    - 값 블록(math_number/text)을 실제 블록으로 만들고
//    - 부모 inputs[NAME] = {name, block: <그 id>, shadow: <그 id>}
//    (2026-07-24: 이걸 빠뜨려 입력칸이 빈 채로 거대하게 렌더링되는 버그 — 대표 발견)
const VAL = (opcode, field, value) => ({opcode, field, value});
const NUM = v => VAL('math_number', 'NUM', v);
const TXT = v => VAL('text', 'TEXT', v);
// 드롭다운 메뉴 블록(예: "마우스 포인터 쪽 보기"의 대상 선택). 메뉴도 shadow 블록이다.
const MENU = (opcode, field, value) => ({opcode, field, value});

/**
 * 블록 배열 → sb3 blocks 객체. 순서대로 next/parent 를 이어 붙인다(하나의 스택).
 * @param {string} prefix id 접두사
 * @param {Array} list [{opcode, inputs, fields}] 순서대로
 * @param {number} x 스택 시작 x
 * @param {number} y 스택 시작 y
 * @returns {object} blocks
 */
const stack = (prefix, list, x = 60, y = 60) => {
    const id = mk(prefix);
    const ids = list.map(() => id());
    const blocks = {};
    list.forEach((b, i) => {
        const inputs = {};
        // 값 입력마다 shadow 블록을 실제로 생성하고 참조를 건다
        Object.keys(b.inputs || {}).forEach(name => {
            const v = b.inputs[name];
            const sid = id();
            blocks[sid] = {
                opcode: v.opcode,
                next: null,
                parent: ids[i],
                inputs: {},
                fields: {[v.field]: {name: v.field, value: String(v.value)}},
                shadow: true,
                topLevel: false
            };
            inputs[name] = {name, block: sid, shadow: sid};
        });
        // C블록 내부(반복/조건 안에 들어가는 블록들) — SUBSTACK 입력에 자식 스택을 건다.
        // 자식은 shadow 가 아니라 실제 블록이므로 {block: <첫블록>, shadow: null}.
        if (b.substack && b.substack.length) {
            const inner = stack(`${prefix}s${i}`, b.substack);
            const innerTop = Object.keys(inner).find(k => inner[k].topLevel);
            Object.keys(inner).forEach(k => {
                // 내부 블록은 최상위가 아니다(워크스페이스에 따로 떠다니면 안 됨)
                if (k === innerTop) { inner[k].topLevel = false; inner[k].parent = ids[i]; delete inner[k].x; delete inner[k].y; }
                blocks[k] = inner[k];
            });
            if (innerTop) inputs.SUBSTACK = {name: 'SUBSTACK', block: innerTop, shadow: null};
        }
        blocks[ids[i]] = {
            opcode: b.opcode,
            next: i < list.length - 1 ? ids[i + 1] : null,
            parent: i > 0 ? ids[i - 1] : null,
            inputs,
            fields: b.fields || {},
            shadow: false,
            topLevel: i === 0,
            ...(i === 0 ? {x, y} : {})
        };
    });
    return blocks;
};

/**
 * 템플릿 정의.
 * key      — 식별자
 * icon     — curo-icons 의 아이콘 이름
 * color    — 카드 강조색
 * blocks() — 스프라이트에 넣을 blocks 객체 (없으면 빈 프로젝트)
 * ext      — 필요한 확장 id(자동 추가). 없으면 [].
 */
export const TEMPLATES = [
    {
        key: 'blank',
        icon: 'spark',
        color: '#8a90a3',
        ext: [],
        blocks: () => ({})
    },
    {
        key: 'move',
        icon: 'code',
        color: '#4c97ff',
        ext: [],
        // 깃발 클릭 → 10만큼 움직이고 → 안녕! 말하기 (첫 성공 경험 30초)
        blocks: () => stack('move', [
            {opcode: 'event_whenflagclicked'},
            {opcode: 'motion_movesteps', inputs: {STEPS: NUM(10)}},
            {opcode: 'looks_sayforsecs', inputs: {MESSAGE: TXT('안녕!'), SECS: NUM(2)}}
        ])
    },
    {
        key: 'keys',
        icon: 'code',
        color: '#9966ff',
        ext: [],
        // 방향키로 움직이기 — 게임의 기본. 스택 4개(위/아래/좌/우)
        blocks: () => Object.assign({},
            stack('kU', [
                {opcode: 'event_whenkeypressed', fields: {KEY_OPTION: {name: 'KEY_OPTION', value: 'up arrow'}}},
                {opcode: 'motion_changeyby', inputs: {DY: NUM(10)}}
            ], 60, 60),
            stack('kD', [
                {opcode: 'event_whenkeypressed', fields: {KEY_OPTION: {name: 'KEY_OPTION', value: 'down arrow'}}},
                {opcode: 'motion_changeyby', inputs: {DY: NUM(-10)}}
            ], 60, 200),
            stack('kR', [
                {opcode: 'event_whenkeypressed', fields: {KEY_OPTION: {name: 'KEY_OPTION', value: 'right arrow'}}},
                {opcode: 'motion_changexby', inputs: {DX: NUM(10)}}
            ], 320, 60),
            stack('kL', [
                {opcode: 'event_whenkeypressed', fields: {KEY_OPTION: {name: 'KEY_OPTION', value: 'left arrow'}}},
                {opcode: 'motion_changexby', inputs: {DX: NUM(-10)}}
            ], 320, 200)
        )
    },
    {
        key: 'chase',
        icon: 'spark',
        color: '#ff6680',
        ext: [],
        // 마우스 따라다니기 — 계속 움직이는 걸 보면 재미있어함
        blocks: () => stack('chase', [
            {opcode: 'event_whenflagclicked'},
            {opcode: 'control_forever', substack: [
                {opcode: 'motion_pointtowards', inputs: {TOWARDS: MENU('motion_pointtowards_menu', 'TOWARDS', '_mouse_')}},
                {opcode: 'motion_movesteps', inputs: {STEPS: NUM(3)}}
            ]}
        ])
    },
    {
        key: 'ai',
        icon: 'brain',
        color: '#14c8a6',
        ext: [],
        // AI 학습 탭으로 유도 — 블록은 깃발+말하기만(학습은 탭에서)
        blocks: () => stack('ai', [
            {opcode: 'event_whenflagclicked'},
            {opcode: 'looks_sayforsecs', inputs: {MESSAGE: TXT('AI 학습 탭에서 나만의 AI를 만들어요!'), SECS: NUM(3)}}
        ]),
        goTab: 'ai'
    },
    {
        key: 'data',
        icon: 'chart',
        color: '#ff8c1a',
        ext: ['cubroidchart'],
        blocks: () => ({}),
        goExtension: true
    },
    {
        key: 'python',
        icon: 'code',
        color: '#306998',
        ext: [],
        blocks: () => ({}),
        goTab: 'python'
    }
];

/**
 * 실행 중인 VM의 현재 스프라이트에 블록을 얹는다.
 * ⭐ 프로젝트를 다시 로드하지 않는다 — 이미 로드된 기본 프로젝트(고양이·배경) 위에 블록만 추가.
 *    createBlock 으로 하나씩 넣고 workspace 갱신을 emit → 사용자가 만든 것처럼 자연스럽게 보인다.
 * @param {object} vm VirtualMachine
 * @param {object} blocks sb3 blocks 객체
 * @returns {boolean} 성공 여부
 */
export const applyBlocksToVm = (vm, blocks) => {
    try {
        if (!vm || !vm.runtime || !blocks) return false;
        const target = vm.editingTarget ||
            (vm.runtime.targets || []).find(t => t && !t.isStage && t.isOriginal !== false);
        if (!target || !target.blocks) return false;
        Object.keys(blocks).forEach(id => {
            target.blocks.createBlock(Object.assign({id}, blocks[id]));
        });
        // 워크스페이스에 반영 + 저장대상으로 표시
        if (vm.emitWorkspaceUpdate) vm.emitWorkspaceUpdate();
        if (vm.runtime.emitProjectChanged) vm.runtime.emitProjectChanged();
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * 템플릿을 defaultProject 자산 배열에 적용한다.
 * @param {Array} assets defaultProject(translator) 결과
 * @param {object} tpl TEMPLATES 항목
 * @returns {Array} blocks 가 채워진 새 자산 배열
 */
export const applyTemplate = (assets, tpl) => {
    if (!tpl || !tpl.blocks) return assets;
    let blocks;
    try {
        blocks = tpl.blocks();
    } catch (e) {
        return assets;
    }
    if (!blocks || Object.keys(blocks).length === 0) return assets;
    try {
        const copy = assets.slice();
        const pi = copy.findIndex(a => a.assetType === 'Project');
        if (pi < 0) return assets;
        const json = JSON.parse(copy[pi].data);
        // targets[0]=Stage, targets[1]=Sprite1 — 스프라이트에 블록을 넣는다
        const sprite = (json.targets || []).find(t => !t.isStage);
        if (!sprite) return assets;
        sprite.blocks = blocks;
        copy[pi] = Object.assign({}, copy[pi], {data: JSON.stringify(json)});
        return copy;
    } catch (e) {
        return assets;
    }
};
